import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Search, 
  Plus, 
  Minus, 
  Trash2, 
  Send, 
  Save,
  ShoppingCart,
  Package,
  ArrowLeft,
  Clock,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import Modal from '../components/Modal';

function OrdineAggiuntivo() {
  const [fornitori, setFornitori] = useState([]);
  const [ordineProdotti, setOrdineProdotti] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [savingOrdine, setSavingOrdine] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [ordineOriginale, setOrdineOriginale] = useState(null);
  const [notePersonalizzate, setNotePersonalizzate] = useState({});
  const [showOrdineOriginale, setShowOrdineOriginale] = useState(false);
  const [showDeleteProdottoModal, setShowDeleteProdottoModal] = useState(false);
  const [prodottoToDelete, setProdottoToDelete] = useState(null);
  const [ultimoProdottoInserito, setUltimoProdottoInserito] = useState(null);
  const [focusSearchAfterAdd, setFocusSearchAfterAdd] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (focusSearchAfterAdd && searchRef.current) {
      const input = searchRef.current;
      setFocusSearchAfterAdd(false);
      requestAnimationFrame(() => input.focus());
    }
  }, [focusSearchAfterAdd]);

  useEffect(() => {
    loadData();
    
    // Controlla se c'è un ordine originale da cui partire
    const ordineDaLocation = location.state?.ordineOriginale;
    if (ordineDaLocation) {
      setOrdineOriginale(ordineDaLocation);
    }
  }, [location.state]);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchProdotti();
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchTerm]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [, fornitoriRes] = await Promise.all([
        axios.get('/api/prodotti', { headers }),
        axios.get('/api/fornitori', { headers })
      ]);

      setFornitori(fornitoriRes.data.data);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      toast.error('Errore nel caricamento dei dati');
    }
  };

  const searchProdotti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/prodotti/search?q=${searchTerm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSearchResults(response.data.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
    }
  };

  const getFornitoreDefault = async (categoria) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/fornitori/default/${categoria}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      // Se non c'è un default, prendi il primo fornitore disponibile per questa categoria
      const fornitoriResponse = await axios.get(`/api/fornitori/categoria/${categoria}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (fornitoriResponse.data.success && fornitoriResponse.data.data.length > 0) {
        return fornitoriResponse.data.data[0];
      }
      
      return null;
    } catch (error) {
      console.error('Errore recupero fornitore default:', error);
      return null;
    }
  };

  const addProdottoToOrdine = async (prodotto) => {
    try {
      const existingIndex = ordineProdotti.findIndex(item => item.id === prodotto.id);
      
      if (existingIndex >= 0) {
        const updated = [...ordineProdotti];
        updated[existingIndex].quantita += 1;
        // Segna questo prodotto come ultimo modificato
        setUltimoProdottoInserito(prodotto.id);
        setOrdineProdotti(updated);
        toast.success(`${prodotto.nome} aggiunto all'ordine aggiuntivo`);
      } else {
        // Trova fornitore default per questa categoria
        let fornitoreDefault = null;
        try {
          fornitoreDefault = await getFornitoreDefault(prodotto.categoria);
        } catch (error) {
          console.error('Errore recupero fornitore default:', error);
          // Continua comunque senza fornitore
        }
        
        const nuovoProdotto = {
          ...prodotto,
          quantita: 1,
          note: prodotto.nota_default || '',
          fornitore_id: fornitoreDefault?.id || null
        };
        
        const updated = [...ordineProdotti, nuovoProdotto];
        // Segna questo prodotto come ultimo inserito
        setUltimoProdottoInserito(prodotto.id);
        setOrdineProdotti(updated);
        toast.success(`${prodotto.nome} aggiunto all'ordine aggiuntivo`);
      }
      
      setSearchTerm('');
      setShowSearchResults(false);
      setFocusSearchAfterAdd(true);
    } catch (error) {
      console.error('Errore aggiunta prodotto:', error);
      toast.error(`Errore nell'aggiunta di ${prodotto.nome}`);
    }
  };

  const updateQuantita = (prodottoId, newQuantita) => {
    if (newQuantita <= 0) {
      removeProdottoFromOrdine(prodottoId);
      return;
    }

    const updated = ordineProdotti.map(item =>
      item.id === prodottoId ? { ...item, quantita: newQuantita } : item
    );
    setOrdineProdotti(updated);
    if (prodottoId === ultimoProdottoInserito) {
      setFocusSearchAfterAdd(true);
    }
  };

  const removeProdottoFromOrdine = (prodottoId) => {
    const prodotto = ordineProdotti.find(item => item.id === prodottoId);
    setProdottoToDelete(prodotto);
    setShowDeleteProdottoModal(true);
  };

  const executeRemoveProdotto = () => {
    if (!prodottoToDelete) return;
    const updated = ordineProdotti.filter(item => item.id !== prodottoToDelete.id);
    setOrdineProdotti(updated);
    toast.success('Prodotto rimosso dall\'ordine');
    setShowDeleteProdottoModal(false);
    setProdottoToDelete(null);
  };

  const updateNote = (prodottoId, note) => {
    const updated = ordineProdotti.map(item =>
      item.id === prodottoId ? { ...item, note } : item
    );
    setOrdineProdotti(updated);
  };

  const updateFornitore = (prodottoId, fornitoreId) => {
    const updated = ordineProdotti.map(item =>
      item.id === prodottoId ? { ...item, fornitore_id: parseInt(fornitoreId) } : item
    );
    setOrdineProdotti(updated);
  };

  const getUltimoProdottoInserito = () => {
    if (!ultimoProdottoInserito) return null;
    return ordineProdotti.find(item => item.id === ultimoProdottoInserito);
  };

  const groupByFornitore = () => {
    const grouped = {};
    // Escludi l'ultimo prodotto inserito dalla lista normale
    const prodottiDaMostrare = ordineProdotti.filter(item => item.id !== ultimoProdottoInserito);
    
    prodottiDaMostrare.forEach(item => {
      const fornitoreId = item.fornitore_id || 'senza_fornitore';
      if (!grouped[fornitoreId]) {
        grouped[fornitoreId] = {
          fornitore_id: item.fornitore_id,
          fornitore: fornitori.find(f => f.id === item.fornitore_id),
          prodotti: []
        };
      }
      grouped[fornitoreId].prodotti.push(item);
    });
    
    // Ordina i prodotti alfabeticamente per ogni fornitore
    Object.keys(grouped).forEach(fornitoreId => {
      grouped[fornitoreId].prodotti.sort((a, b) => a.nome.localeCompare(b.nome));
    });
    
    return grouped;
  };

  const groupOrdineOriginaleByCategoria = () => {
    if (!ordineOriginale?.prodotti) return {};
    
    const grouped = {};
    ordineOriginale.prodotti.forEach(item => {
      if (!grouped[item.categoria]) {
        grouped[item.categoria] = [];
      }
      grouped[item.categoria].push(item);
    });
    
    // Ordina i prodotti alfabeticamente per ogni categoria
    Object.keys(grouped).forEach(categoria => {
      grouped[categoria].sort((a, b) => a.nome.localeCompare(b.nome));
    });
    
    return grouped;
  };

  const handleNotaChange = (categoria, nota) => {
    setNotePersonalizzate({
      ...notePersonalizzate,
      [categoria]: nota
    });
  };

  const saveOrdineAggiuntivo = async () => {
    if (ordineProdotti.length === 0) {
      toast.error('Aggiungi almeno un prodotto all\'ordine aggiuntivo');
      return;
    }

    setSavingOrdine(true);
    try {
      const token = localStorage.getItem('token');
      
      // Crea sempre un nuovo ordine aggiuntivo
      const prodottiDaSalvare = ordineProdotti.map(item => ({
          id: item.id,
          quantita: item.quantita,
          note: item.note || '',
          fornitore_id: item.fornitore_id || null,
          tipologia: item.tipologia || null
      }));
      
      const ordineResponse = await axios.post('/api/ordini', {
        prodotti: prodottiDaSalvare,
        note_personalizzate: notePersonalizzate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (ordineResponse.data.success) {
        toast.success('Ordine aggiuntivo salvato con successo!');
        navigate('/ordini');
      } else {
        toast.error('Errore nel salvataggio dell\'ordine aggiuntivo');
      }
    } catch (error) {
      console.error('❌ [ORDINE_AGGIUNTIVO] Errore salvataggio ordine aggiuntivo:', error);
      console.error('❌ [ORDINE_AGGIUNTIVO] Dettagli errore:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Errore nel salvataggio dell\'ordine aggiuntivo');
    } finally {
      setSavingOrdine(false);
    }
  };

  const sendOrdineAggiuntivo = async () => {
    if (ordineProdotti.length === 0) {
      toast.error('Aggiungi almeno un prodotto all\'ordine aggiuntivo');
      return;
    }

    setConfirmAction(() => () => executeSendOrdineAggiuntivo());
    setShowConfirmModal(true);
  };

  const executeSendOrdineAggiuntivo = async () => {
    setShowConfirmModal(false);
    setSendingWhatsApp(true);

    try {
      const token = localStorage.getItem('token');
      
      // Crea nuovo ordine aggiuntivo
      const prodottiDaSalvare = ordineProdotti.map(item => ({
          id: item.id,
          quantita: item.quantita,
          note: item.note || '',
          fornitore_id: item.fornitore_id || null,
          tipologia: item.tipologia || null
      }));
      
      const ordineResponse = await axios.post('/api/ordini', {
        prodotti: prodottiDaSalvare,
        note_personalizzate: notePersonalizzate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!ordineResponse.data.success) {
        throw new Error('Errore nella creazione dell\'ordine aggiuntivo');
      }

      const ordineId = ordineResponse.data.id || ordineResponse.data.data?.id;

      // Genera link per ogni fornitore con testo specifico per ordine aggiuntivo
      const groupedByFornitore = groupByFornitore();
      const linkWhatsApp = [];
      let successCount = 0;
      
      for (const gruppo of Object.values(groupedByFornitore)) {
        try {
          if (!gruppo.fornitore) {
            console.warn(`Prodotti senza fornitore assegnato trovati`);
            continue;
          }

          // Prendi la nota specifica per questo fornitore
          const notaFornitore = notePersonalizzate[`fornitore_${gruppo.fornitore.id}`] || '';

          // Genera messaggio specifico per ordine aggiuntivo e link
          const messaggioResponse = await axios.post('/api/whatsapp/genera-messaggio-aggiuntivo', {
            ordine_id: ordineId,
            fornitore_id: gruppo.fornitore.id,
            ordine_originale_id: ordineOriginale?.id,
            intro_custom: `Ciao ${gruppo.fornitore.nome}! Aggiunta all'ordine precedente:`,
            outro_custom: 'Grazie e a presto!',
            nota_personalizzata: notaFornitore
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (messaggioResponse.data.success) {
            const messaggioData = messaggioResponse.data.data;
            linkWhatsApp.push({
              fornitore_id: gruppo.fornitore.id,
              fornitore: gruppo.fornitore.nome,
              link: messaggioData.whatsappLink,
              messaggio: messaggioData.messaggio
            });
            successCount++;
          }
        } catch (error) {
          console.error(`Errore generazione link per fornitore ${gruppo.fornitore?.nome}:`, error);
        }
      }

      if (linkWhatsApp.length > 0) {
        // Mostra i link generati
        toast.success(`${linkWhatsApp.length} link WhatsApp aggiuntivi generati! Apertura in corso...`, { duration: 5000 });
        
        // Apri TUTTI i link con un ritardo di 1 secondo tra uno e l'altro
        linkWhatsApp.forEach((link, index) => {
          setTimeout(() => {
            window.open(link.link, '_blank');
            toast.success(`Aperto WhatsApp per ${link.fornitore}`, { duration: 3000 });
          }, index * 1000); // Ritardo di 1 secondo per ogni link
        });
        
        // Aggiorna lo stato dell'ordine a 'sent' dopo l'apertura dei link
        setTimeout(async () => {
          try {
            await axios.put(`/api/ordini/${ordineId}/stato`, {
              stato: 'sent'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ Stato ordine aggiuntivo aggiornato a "sent"');
          } catch (error) {
            console.error('❌ Errore aggiornamento stato ordine aggiuntivo:', error);
          }
        }, linkWhatsApp.length * 1000 + 500); // Dopo l'ultimo link + 500ms
      }

      if (successCount > 0) {
        toast.success(`Ordine aggiuntivo creato e ${successCount} messaggi inviati!`);
        navigate('/ordini');
      } else {
        toast.error('Errore nell\'invio dei messaggi WhatsApp');
      }
    } catch (error) {
      console.error('Errore invio ordine aggiuntivo:', error);
      toast.error('Errore nell\'invio dell\'ordine aggiuntivo');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6 pb-6">
      {/* Header - compatto su mobile */}
      <div className="flex items-center space-x-2 sm:space-x-4 flex-wrap gap-2">
        <button
          onClick={() => navigate('/ordini')}
          className="btn btn-outline btn-sm flex-shrink-0"
        >
          <ArrowLeft className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Torna agli Ordini</span>
          <span className="sm:hidden">Indietro</span>
        </button>
        <div className="flex-1 min-w-0">
          {/* <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Ordine Aggiuntivo</h1>
          <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-500 line-clamp-2">
            {ordineOriginale 
              ? `Aggiungi prodotti all'ordine #${ordineOriginale.id} del ${new Date(ordineOriginale.data_ordine).toLocaleDateString('it-IT')}`
              : 'Aggiungi prodotti da inviare ai fornitori'
            }
          </p> */}
        </div>
      </div>

      {/* Info Ordine Originale - compatto su mobile */}
      {ordineOriginale && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-1.5 sm:mr-2 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-xs sm:text-sm font-medium text-blue-900 truncate">
                  Ordine Originale #{ordineOriginale.id}
                </h3>
                <p className="text-xs text-blue-700 line-clamp-1">
                  {new Date(ordineOriginale.data_ordine).toLocaleDateString('it-IT')} • 
                  {ordineOriginale.prodotti?.length || 0} prodotti
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowOrdineOriginale(!showOrdineOriginale)}
              className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showOrdineOriginale ? (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Nascondi
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Mostra
                </>
              )}
            </button>
          </div>
          
          {/* Ordine Originale Espanso */}
          {showOrdineOriginale && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="space-y-4">
                {Object.entries(groupOrdineOriginaleByCategoria()).map(([categoria, prodotti]) => (
                  <div key={categoria} className="bg-white rounded-lg p-3">
                    <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <Package className="h-3 w-3 mr-1" />
                      {categoria.replace('_', ' ').toUpperCase()}
                    </h4>
                    <div className="space-y-1">
                      {prodotti.map((prodotto) => (
                        <div key={prodotto.id} className="text-sm text-gray-600">
                          {prodotto.quantita} {prodotto.tipologia_scelta ? `${prodotto.tipologia_scelta} ` : ''}{prodotto.nome}
                        </div>
                      ))}
                      
                      {/* Mostra note personalizzate per questa categoria se presenti */}
                      {ordineOriginale.note_personalizzate && ordineOriginale.note_personalizzate[categoria] && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700 border-l-2 border-blue-300">
                          <span className="font-medium">📝 Note:</span> {ordineOriginale.note_personalizzate[categoria]}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search - sticky su mobile quando tastiera aperta */}
      <div className="bg-white shadow rounded-lg p-3 sm:p-6 sticky top-0 sm:static z-10">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          </div>
          <input
            ref={searchRef}
            type="text"
            className="block w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="Cerca prodotti..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: '16px' }} // Previene zoom su iOS
          />
        </div>

        {/* Search Results - visibile sopra tastiera */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-lg max-h-[calc(100vh-200px)] sm:max-h-60 overflow-y-auto">
            {searchResults.map((prodotto) => (
              <button
                key={prodotto.id}
                onClick={() => addProdottoToOrdine(prodotto)}
                className="w-full px-4 py-2.5 sm:py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 active:bg-gray-100"
                style={{ touchAction: 'manipulation' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{prodotto.nome}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {prodotto.tipologia} • {prodotto.categoria}
                    </p>
                  </div>
                  <Plus className="h-4 w-4 text-blue-600 flex-shrink-0 ml-2" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Order Summary */}
      {ordineProdotti.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <ShoppingCart className="h-5 w-5 mr-2" />
              Ordine Aggiuntivo ({ordineProdotti.length} prodotti)
            </h2>
          </div>

          {/* Box Ultimo Prodotto Inserito - sempre visibile in alto */}
          {getUltimoProdottoInserito() && (() => {
            const ultimoProdotto = getUltimoProdottoInserito();
            const fornitoriCategoria = fornitori.filter(f => 
              Array.isArray(f.categorie) && f.categorie.includes(ultimoProdotto.categoria)
            );
            
            return (
              <div className="p-4 sm:p-6 bg-blue-50 border-b-2 border-blue-300">
                <div className="flex items-center mb-3">
                  <div className="h-2 w-2 bg-blue-600 rounded-full mr-2 animate-pulse"></div>
                  <h3 className="text-sm font-semibold text-blue-900">Ultimo prodotto inserito</h3>
                </div>
                
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  {/* Nome prodotto con cestino a destra */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm font-medium text-gray-900 flex-1 break-words">{ultimoProdotto.nome}</p>
                    {/* Cestino piccolo a destra del nome */}
                    <button
                      onClick={() => removeProdottoFromOrdine(ultimoProdotto.id)}
                      className="flex items-center justify-center min-w-[32px] min-h-[32px] sm:min-w-[28px] sm:min-h-[28px] p-1.5 sm:p-1 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                      style={{ touchAction: 'manipulation' }}
                      aria-label="Rimuovi prodotto"
                    >
                      <Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                    </button>
                  </div>
                  
                  <div className="flex items-start justify-between gap-3">
                    {/* Tipologia, fornitore e note - con più spazio */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">{ultimoProdotto.tipologia}</p>
                      
                      {/* Dropdown Fornitore */}
                      {fornitoriCategoria.length > 1 && (
                        <select
                          value={ultimoProdotto.fornitore_id || ''}
                          onChange={(e) => updateFornitore(ultimoProdotto.id, e.target.value)}
                          className="mt-1 text-xs border border-blue-300 rounded px-2 py-1 w-full bg-white focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Seleziona fornitore...</option>
                          {fornitoriCategoria.map(f => (
                            <option key={f.id} value={f.id}>
                              {f.nome}
                            </option>
                          ))}
                        </select>
                      )}
                      
                      {fornitoriCategoria.length === 1 && (
                        <p className="mt-1 text-xs text-blue-600">
                          Fornitore: {fornitoriCategoria[0].nome}
                        </p>
                      )}
                      
                      <input
                        type="text"
                        placeholder="Note (opzionale)"
                        value={ultimoProdotto.note || ''}
                        onChange={(e) => updateNote(ultimoProdotto.id, e.target.value)}
                        className="mt-1 text-xs border border-gray-300 rounded px-2 py-1 w-full"
                      />
                    </div>
                    
                    {/* Controlli quantità - a destra */}
                    <div className="flex items-center flex-shrink-0">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => updateQuantita(ultimoProdotto.id, ultimoProdotto.quantita - 1)}
                          className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                          style={{ touchAction: 'manipulation' }}
                          aria-label="Diminuisci quantità"
                        >
                          <Minus className="h-5 w-5 sm:h-4 sm:w-4" />
                        </button>
                        <span className="text-base sm:text-sm font-medium text-gray-900 min-w-[3rem] sm:min-w-[2rem] text-center">
                          {ultimoProdotto.quantita}
                        </span>
                        <button
                          onClick={() => updateQuantita(ultimoProdotto.id, ultimoProdotto.quantita + 1)}
                          className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                          style={{ touchAction: 'manipulation' }}
                          aria-label="Aumenta quantità"
                        >
                          <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="divide-y divide-gray-200">
            {Object.entries(groupByFornitore()).map(([fornitoreId, gruppo]) => {
              // Mostra anche i prodotti senza fornitore, ma solo se ci sono prodotti nel gruppo
              if (gruppo.prodotti.length === 0) return null;
              
              return (
                <div key={fornitoreId} className="p-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Package className="h-4 w-4 mr-2" />
                    {gruppo.fornitore ? gruppo.fornitore.nome : 'Senza fornitore assegnato'}
                  </h3>
                
                <div className="space-y-3">
                  {gruppo.prodotti.map((item) => {
                    // Filtra fornitori per questa categoria
                    const fornitoriCategoria = fornitori.filter(f => 
                      Array.isArray(f.categorie) && f.categorie.includes(item.categoria)
                    );
                    
                    return (
                      <div key={item.id} className="bg-gray-50 rounded-lg p-3">
                        {/* Nome prodotto con cestino a destra */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-gray-900 flex-1 break-words">{item.nome}</p>
                          {/* Cestino piccolo a destra del nome */}
                          <button
                            onClick={() => removeProdottoFromOrdine(item.id)}
                            className="flex items-center justify-center min-w-[32px] min-h-[32px] sm:min-w-[28px] sm:min-h-[28px] p-1.5 sm:p-1 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                            style={{ touchAction: 'manipulation' }}
                            aria-label="Rimuovi prodotto"
                          >
                            <Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                          </button>
                        </div>
                        
                        <div className="flex items-start justify-between gap-3">
                          {/* Tipologia, fornitore e note - con più spazio */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500">{item.tipologia}</p>
                            
                            {/* Dropdown Fornitore */}
                            {fornitoriCategoria.length > 1 && (
                              <select
                                value={item.fornitore_id || ''}
                                onChange={(e) => updateFornitore(item.id, e.target.value)}
                                className="mt-1 text-xs border border-blue-300 rounded px-2 py-1 w-full bg-white focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="">Seleziona fornitore...</option>
                                {fornitoriCategoria.map(f => (
                                  <option key={f.id} value={f.id}>
                                    {f.nome}
                                  </option>
                                ))}
                              </select>
                            )}
                            
                            {fornitoriCategoria.length === 1 && (
                              <p className="mt-1 text-xs text-blue-600">
                                Fornitore: {fornitoriCategoria[0].nome}
                              </p>
                            )}
                            
                            <input
                              type="text"
                              placeholder="Note (opzionale)"
                              value={item.note || ''}
                              onChange={(e) => updateNote(item.id, e.target.value)}
                              className="mt-1 text-xs border border-gray-300 rounded px-2 py-1 w-full"
                            />
                          </div>
                          
                          {/* Controlli quantità - a destra */}
                          <div className="flex items-center flex-shrink-0">
                            <div className="flex items-center space-x-1 sm:space-x-2">
                              <button
                                onClick={() => updateQuantita(item.id, item.quantita - 1)}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                                style={{ touchAction: 'manipulation' }}
                                aria-label="Diminuisci quantità"
                              >
                                <Minus className="h-5 w-5 sm:h-4 sm:w-4" />
                              </button>
                              <span className="text-base sm:text-sm font-medium text-gray-900 min-w-[3rem] sm:min-w-[2rem] text-center">
                                {item.quantita}
                              </span>
                              <button
                                onClick={() => updateQuantita(item.id, item.quantita + 1)}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                                style={{ touchAction: 'manipulation' }}
                                aria-label="Aumenta quantità"
                              >
                                <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Campo Note Personalizzate per Fornitore - solo se c'è un fornitore */}
                  {gruppo.fornitore && (
                    <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        📝 Note aggiuntive per {gruppo.fornitore.nome}
                      </label>
                      <textarea
                        value={notePersonalizzate[`fornitore_${fornitoreId}`] || ''}
                        onChange={(e) => handleNotaChange(`fornitore_${fornitoreId}`, e.target.value)}
                        placeholder="Es: Consegna entro le 10:00, prodotto fresco, confezione speciale..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        rows="2"
                      />
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={saveOrdineAggiuntivo}
                disabled={savingOrdine}
                className="flex-1 btn bg-blue-600 hover:bg-blue-700 text-white h-10 py-2 px-4"
              >
                {savingOrdine ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salva Ordine Aggiuntivo
              </button>
              <button
                onClick={sendOrdineAggiuntivo}
                disabled={sendingWhatsApp}
                className="flex-1 btn bg-orange-600 hover:bg-orange-700 text-white h-10 py-2 px-4"
              >
                {sendingWhatsApp ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Invia Ordine Aggiuntivo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {ordineProdotti.length === 0 && (
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun prodotto nell'ordine aggiuntivo</h3>
          <p className="mt-1 text-sm text-gray-500">
            Inizia a cercare e aggiungere i prodotti da aggiungere all'ordine precedente.
          </p>
        </div>
      )}

      {/* Modale di conferma */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Conferma Ordine Aggiuntivo"
        message={`Sei sicuro di voler inviare questo ordine aggiuntivo?\n\nI fornitori riceveranno un messaggio chiaro che si tratta di un'aggiunta all'ordine precedente.`}
        type="warning"
        confirmText="Invia Aggiunta"
        cancelText="Annulla"
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
      />

      {/* Modale di conferma eliminazione prodotto */}
      <Modal
        isOpen={showDeleteProdottoModal}
        onClose={() => {
          setShowDeleteProdottoModal(false);
          setProdottoToDelete(null);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler rimuovere "${prodottoToDelete?.nome}" dall'ordine?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Sì, Rimuovi"
        cancelText="Annulla"
        onConfirm={executeRemoveProdotto}
      />
    </div>
  );
}

export default OrdineAggiuntivo;
